// Workbook export for the two Manpower surfaces that travel outside the app:
// the crew schedule (#384) and Gap Analysis (#396 slice 4).
//
// ── Why one module for two different tables ──────────────────────────────────
// #396 says the export overlaps #384 and is "worth building once", so the
// MECHANISM lives here once: the cell primitives, column fitting, the header
// block, the file stamp, and the download. What is deliberately NOT shared is
// the sheet composition — a crew grid is jobs × dates and Gap Analysis is
// weeks × metrics plus a per-job table, and forcing one builder over both
// would produce a worse artifact than either. Two builders, one toolkit.
//
// ⚠️ NOTHING HERE RECOMPUTES A NUMBER. Every figure is passed in already
// projected by the surface that drew it, so the file and the screen cannot
// disagree — and so the week-bucket fix in #477 lands in one place rather than
// having to be repeated here.

import * as XLSX from 'xlsx';
import { todayInputValue, fmtDate, fmtDateTime } from '@/lib/dates';
import { weekLabel } from '@/modules/projects/lib/weekLabel';
import type { GapAnalysis } from '@/lib/apiClient';

export type Cell = string | number;

/**
 * The date in the FILE NAME, from local components.
 *
 * Same reason `trainingReport.ts` does it: `toISOString()` is UTC, so an
 * Eastern user exporting after 8pm stamps every file with TOMORROW and files
 * two reports that look like they came from different days.
 */
export const stamp = () => todayInputValue();

/** Size columns to their widest cell so nothing lands as "####" or truncated. */
export function fitColumns(rows: Cell[][]): XLSX.ColInfo[] {
  const widths: number[] = [];
  for (const row of rows) {
    row.forEach((cell, i) => {
      widths[i] = Math.max(widths[i] ?? 10, Math.min(String(cell ?? '').length + 2, 60));
    });
  }
  return widths.map((wch) => ({ wch }));
}

export function sheet(
  aoa: Cell[][],
  opts: { filter?: boolean; filterRows?: number } = {},
): XLSX.WorkSheet {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = fitColumns(aoa);
  // Autofilter only where row 1 really is a header — a label/value summary
  // sheet with a filter on it is nonsense.
  //
  // ⚠️ `filterRows` exists because a sheet's last row is not always its last
  // DATA row. Where explanatory notes sit beneath the table, extending the
  // filter to the end of the sheet swallows them: filtering by any column
  // hides the notes — including the one warning that weekly figures are peaks
  // and must not be summed — and sorting can shuffle them up into the data.
  // The notes that survive a filter are the only ones worth writing.
  if (opts.filter && aoa.length > 1) {
    const lastRow = Math.max(0, (opts.filterRows ?? aoa.length) - 1);
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: (aoa[0]?.length ?? 1) - 1 } }),
    };
  }
  return ws;
}

/**
 * A blank cell for a figure nobody has supplied.
 *
 * ⚠️ Load-bearing, and the reason this is a named helper rather than `?? 0`.
 * On the Gap Analysis tab a supply box the manpower lead has never filled in shows "—" and
 * its gap reads "not set", never 0 — a missing supply figure is not a
 * shortfall of the whole demand. A workbook that wrote 0 there would state a
 * crisis the app is careful never to state, and would total and chart as if it
 * were real.
 */
export const blank = (v: number | null | undefined): Cell => (v === null || v === undefined ? '' : v);

function save(wb: XLSX.WorkBook, filename: string) {
  XLSX.writeFile(wb, filename);
}

// ─── Crew schedule (#384) ────────────────────────────────────────────────────

export interface ScheduleExportColumn {
  kind: 'day' | 'week';
  /** Header text exactly as the grid prints it, e.g. "Mo 9/15" or "wk of 9/14". */
  label: string;
}

export interface ScheduleExportRow {
  sectionLabel: string;
  jobNumber: string;
  title: string;
  /**
   * Which line of the grid this row is — and whether it is addable.
   *
   * ⚠️ One job is not always one row. A fabrication job draws two crew lines
   * (shop and field) and a maintenance parent draws a read-only Σ of its
   * sub-projects, which the grid marks with a `Σ` and a subtotal tooltip that
   * a spreadsheet cell cannot carry. Without this column a reader summing the
   * sheet double-counts every maintenance job, because the parent's roll-up
   * and its children are both present.
   */
  crewLine: string;
  pm: string | null;
  /** Badges the grid paints on the row — SYSTAC, Needs Scheduling, Maintenance… */
  flags: string[];
  travelJob: boolean;
  /** One value per column, already projected by the page's own colValue(). */
  crew: number[];
}

export interface ScheduleExportInput {
  columns: ScheduleExportColumn[];
  rows: ScheduleExportRow[];
  sectionTotals: { label: string; totals: number[] }[];
  grandTotals: number[];
  availableMp: number;
  scopeLabel: string;
  rangeLabel: string;
  /** Active job search, if any — named so the reader knows the file is filtered. */
  search?: string | null;
  /** Active PM filter, if any. */
  pmFilter?: string | null;
  /** Travel/Local row filter, when narrowed. Removes jobs and lowers every
   *  total, exactly as the search does, so it trips the FILTERED note too. */
  travelFilter?: 'travel' | 'local' | null;
  /**
   * Labels of division/section chips the user has switched off.
   *
   * ⚠️ These remove whole sections of jobs and lower every total, exactly as a
   * search does, but they are far easier to forget: a chip toggled off earlier
   * in the session leaves no text on screen saying so. A workbook that named
   * only the search would present a partial company as the whole one.
   */
  hiddenSections?: string[];
  /** The cross-division SYSTAC chip, which hides pursuit rows inside every section. */
  systacHidden?: boolean;
  /**
   * The "Start ≠ Crew" chip (#385).
   *
   * ⚠️ The most aggressive narrowing on the board, and the easiest to export by
   * accident, because it ISOLATES rather than hides: it is switched on precisely
   * to work a list down, and on prod it reduces the grid to roughly half its
   * rows. A workbook taken in that state names a fraction of the company and
   * computes Peak crew and Headroom over it.
   */
  mismatchOnly?: boolean;
  /**
   * The grid is showing the DRAFT overlay (Edit Schedule mode).
   *
   * ⚠️ This is a publication boundary, not a display preference. Staged crew is
   * deliberately invisible to PMs until someone presses Publish, and the draft
   * feed is not even scoped to the person looking at it — the overlay carries
   * every scheduler's staged cells. A workbook taken in that state therefore
   * holds other people's unpublished plans while looking exactly like the live
   * schedule, so it is marked in the filename, the title and a note rather than
   * relying on whoever exported it to remember.
   */
  draft?: boolean;
  /** Fabrication lens only: the shop/field capacity split. */
  fab?: { fabTotals: number[]; installTotals: number[] };
}

/**
 * ⚠️ The granularity note is not decoration.
 *
 * At 6+ weeks the grid switches from a column per day to a column per week
 * holding that week's PEAK day, and the totals row does the same. Peaks do not
 * add up: a reader who sums eight weekly columns gets a number that means
 * nothing. The sheet says so on both the summary and the totals.
 */
const PEAK_NOTE =
  'Weekly columns hold that week’s PEAK day, not its total. Peaks do not add across weeks — do not sum a weekly row.';

export function buildScheduleWorkbook(input: ScheduleExportInput): XLSX.WorkBook {
  const { columns, rows, sectionTotals, grandTotals, availableMp, fab } = input;
  const colHeads = columns.map((c) => c.label);
  const weekly = columns.some((c) => c.kind === 'week');
  const peak = grandTotals.length ? Math.max(...grandTotals) : 0;

  const summary: Cell[][] = [
    [input.draft
      ? 'Project Tracker — crew schedule — UNPUBLISHED DRAFT'
      : 'Project Tracker — crew schedule', ''],
    ['Generated', fmtDate(stamp())],
    ['Window', input.rangeLabel],
    ['View', input.scopeLabel],
    ['Granularity', weekly ? 'One column per week (weekly peak day)' : 'One column per day'],
    ['', ''],
    // Jobs and rows are different numbers: a fabrication job contributes two
    // crew lines. Reporting only the row count would overstate the job count.
    ['Jobs on this sheet', new Set(rows.map((r) => r.jobNumber)).size],
    ['Crew lines on this sheet', rows.length],
    ['Peak crew in the window', peak],
    ['Total field manpower available', availableMp],
    ['Headroom at the peak', availableMp - peak],
    ['', ''],
  ];
  if (input.search) summary.push(['Filtered by job search', input.search]);
  if (input.pmFilter) summary.push(['Filtered by PM', input.pmFilter]);
  if (input.travelFilter) summary.push(['Job type', input.travelFilter === 'travel' ? 'Travel jobs only' : 'Local jobs only']);
  if (input.hiddenSections?.length) summary.push(['Divisions hidden', input.hiddenSections.join(', ')]);
  if (input.systacHidden) summary.push(['SYSTAC pursuits', 'Hidden']);
  if (input.mismatchOnly) summary.push(['Start vs crew', 'Only jobs whose planned start disagrees with their first booked crew day']);
  // Every one of these removes jobs and lowers the totals, so every one of them
  // has to trip the warning — not just the two the reader can still see.
  if (input.search || input.pmFilter || input.travelFilter || input.hiddenSections?.length
      || input.systacHidden || input.mismatchOnly) {
    summary.push(['Note', 'This file is FILTERED. The totals reflect only the jobs listed, not the whole company.']);
  }
  if (input.draft) {
    summary.push(['Status', 'UNPUBLISHED DRAFT — do not distribute']);
    summary.push(['Note', 'This was taken in Edit Schedule mode. It contains staged crew changes that have NOT been published, from every scheduler working on the board — not only the person who exported it. PMs cannot see these figures in the Project Tracker. Publish first, then export again.']);
  }
  summary.push(['Note', 'Crew figures are what the schedule asks for, as entered in the Project Tracker.']);
  // ⚠️ The two ways the Crew schedule sheet does not add up, stated where a
  // reader meets them rather than left to be discovered by a wrong total.
  summary.push(['Note', 'A fabrication job has TWO rows — Fabrication (shop) and Installation (field) — because the schedule plans them separately. See the Crew line column.']);
  summary.push(['Note', 'A maintenance job’s parent row is a roll-up of its sub-projects and is marked "do not add" in the Crew line column. Its sub-projects are listed too, so adding both counts that crew twice. The Totals sheet already counts it once.']);
  if (weekly) summary.push(['Note', PEAK_NOTE]);

  const grid: Cell[][] = [
    ['Division', 'Job #', 'Job', 'Crew line', 'PM', 'Travel job', 'Flags', ...colHeads],
    ...rows.map((r) => [
      r.sectionLabel,
      r.jobNumber,
      r.title,
      r.crewLine,
      r.pm ?? '',
      r.travelJob ? 'Yes' : 'No',
      r.flags.join(', '),
      ...r.crew,
    ]),
  ];

  const pad = (label: string): Cell[] => [label, '', '', '', '', ''];
  const totals: Cell[][] = [
    ['Totals', '', '', '', '', '', ...colHeads],
    ...sectionTotals.map((s) => [...pad(s.label), ...s.totals]),
    [...pad('Grand total'), ...grandTotals],
  ];
  if (fab) {
    totals.push([...pad('Shop (fabrication)'), ...fab.fabTotals]);
    totals.push([...pad('Field (installation)'), ...fab.installTotals]);
  }
  totals.push([...pad('Total field MP available'), ...grandTotals.map(() => availableMp)]);
  totals.push([...pad('Headroom'), ...grandTotals.map((t) => availableMp - t)]);
  if (weekly) {
    totals.push([]);
    totals.push(['Note', PEAK_NOTE]);
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(summary), 'Summary');
  XLSX.utils.book_append_sheet(wb, sheet(grid, { filter: true }), 'Crew schedule');
  XLSX.utils.book_append_sheet(wb, sheet(totals), 'Totals');
  return wb;
}

export function exportScheduleXlsx(input: ScheduleExportInput) {
  // The marker goes in the FILENAME as well as the sheet, because a file gets
  // forwarded by name long before anyone opens the Summary tab.
  save(
    buildScheduleWorkbook(input),
    `crew-schedule-${input.draft ? 'UNPUBLISHED-DRAFT-' : ''}${stamp()}.xlsx`,
  );
}

// ─── Gap Analysis (#396 slice 4) ─────────────────────────────────────────────

/**
 * The line the workbook carries about where the supply numbers come from.
 *
 * The tab states this permanently on screen and the API sends `derived: false`
 * so a consumer cannot miss it. A file that travels to a reader who never saw
 * the tab would present seven hand-typed numbers as though the system computed
 * them, which is the one thing every layer of this feature has refused to do.
 */
const SUPPLY_BASIS =
  'Demand is measured, supply is maintained. Everything "needed" is computed from the crew schedule. Everything "available" is typed by hand and is not calculated from any roster.';

export interface GapExportOptions {
  /**
   * What the Fabrication filter took out of this workbook — jobs and pursuits
   * counted SEPARATELY, because they are missing from different parts of it.
   *
   * ⚠️ The CALLER hands us the already-filtered lists AND the already-filtered
   * weeks; this is only so the Summary can say what is gone. A workbook that
   * silently drops rows a reader would expect is the same failure as a filtered
   * crew schedule that does not admit it — and this filter is ON by default, so
   * a file exported by someone who never touched a control is short of jobs and
   * carries narrowed totals.
   *
   * ⚠️ TWO NUMBERS, NOT ONE, and the split is the fix for a real hole. `jobs`
   * are rows missing from the two job sheets. `pursuits` is demand missing from
   * the three "incl. SYSTAC" columns — which this builder writes on EVERY
   * export, whatever the screen's pursuit toggle was set to. A single count
   * taken from the screen therefore described the wrong thing: with the
   * pursuits switched off and no Fabrication jobs in the horizon it was zero,
   * this whole section was skipped, and the file narrowed its own forecast
   * columns with nothing anywhere saying so. (Codex on #500.)
   */
  fabExcluded?: { jobs: number; pursuits: number };
}

export function buildGapWorkbook(
  data: GapAnalysis,
  weeks: number,
  opts: GapExportOptions = {},
): XLSX.WorkBook {
  const weekHeads = data.weeks.map((w) => weekLabel(w.weekStart));

  const summary: Cell[][] = [
    ['Project Tracker — manpower gap analysis', ''],
    ['Generated', fmtDate(stamp())],
    ['Horizon', `${weeks} weeks`],
    ['Supply last updated', data.supply.updatedAt ? fmtDateTime(data.supply.updatedAt) : 'never set'],
    ...(opts.fabExcluded?.jobs
      ? ([[
          'Fabrication jobs',
          `${opts.fabExcluded.jobs} excluded — Fabrication runs its own crew. They are out of the job sheets AND out of every weekly figure, the same as on screen.`,
        ]] as Cell[][])
      : []),
    // ⚠️ Its own line, and it is NOT conditional on any pursuit being listed in
    // this file. The three "incl. SYSTAC" columns are always written, so a
    // Fabrication bid's forecast is always missing from them once the filter is
    // on — including in the common export where the pursuits were never
    // switched on and no pursuit appears on any sheet.
    ...(opts.fabExcluded?.pursuits
      ? ([[
          'Fabrication pursuits',
          `${opts.fabExcluded.pursuits} excluded — their bid forecast is out of the three "incl. SYSTAC" columns on the Week by week sheet, whether or not this file lists any pursuits.`,
        ]] as Cell[][])
      : []),
    // ⚠️ Says the pursuits are in the file. The job sheets carry a Status
    // column and the Week by week sheet always carries its three "incl. SYSTAC"
    // columns, but only this line says whether the ROWS are bids — and a
    // workbook holding unawarded work has to say so on its first sheet.
    ...(data.systacTravelJobs.length + data.systacLocalJobs.length
      ? ([[
          'SYSTAC pursuits',
          `${data.systacTravelJobs.length + data.systacLocalJobs.length} listed on the job sheets as bids — nothing has been awarded, and no gap is computed from them.`,
        ]] as Cell[][])
      : []),
    ['', ''],
    ['Field manpower — maintained by hand', '', ''],
    ['Measure', 'People', 'Feeds the gap'],
    ...data.supply.metrics.map((m): Cell[] => [m.label, blank(m.value), m.countsTowardGap ? 'Yes' : 'No']),
    ['', ''],
    ['Note', SUPPLY_BASIS],
    ['Note', data.supply.basis],
    ['Note', 'A blank figure means nobody has set it yet. It is not a zero, and the gap for it reads "not set" rather than counting the whole demand as a shortfall.'],
  ];

  const weekRows: Cell[][] = [
    [
      'Week of', 'Total needed', 'Total available', 'Total gap',
      'Travelers needed', 'Travelers available', 'Traveler gap',
      'Traveling leaders needed', 'Traveling leaders available', 'Traveling leader gap',
      'Local crew needed', 'Local crew available', 'Local crew gap',
      'Local leaders needed', 'Local leaders available', 'Local leader gap',
      'Active travel jobs', 'Active local jobs', 'Traveler share',
      'Total needed incl. SYSTAC', 'Travelers needed incl. SYSTAC', 'Local crew needed incl. SYSTAC',
    ],
    ...data.weeks.map((w): Cell[] => [
      weekLabel(w.weekStart),
      w.totalNeeded, blank(w.totalAvailable), blank(w.totalGap),
      w.travelersNeeded, blank(w.travelersAvailable), blank(w.travelerGap),
      w.travelingLeadersNeeded, blank(w.travelingLeadersAvailable), blank(w.travelingLeaderGap),
      w.localCrewNeeded, blank(w.localCrewAvailable), blank(w.localCrewGap),
      w.localLeadersNeeded, blank(w.localLeadersAvailable), blank(w.localLeaderGap),
      w.activeTravelJobs, w.activeLocalJobs,
      `${Math.round(w.travelerShareOfDemand * 100)}%`,
      w.totalNeededWithSystac, w.travelersNeededWithSystac, w.localCrewNeededWithSystac,
    ]),
    [],
    ['Note', 'A positive gap is a shortfall. A blank gap means the matching supply figure has not been set.'],
    // ⚠️ In a spreadsheet somebody WILL add these columns and find they don't
    // reconcile. Each is its own busiest day, so the sum is not the total.
    ['Note', 'Every figure is that week’s peak day, taken on its own. Travelers and Local crew will not always add up to Total — the busiest day for the company need not be the busiest day for either group.'],
    // ⚠️ The three SYSTAC columns are the one place on this sheet where a
    // number describes work nobody has been awarded. They are always written,
    // whatever the screen toggle is set to, because a column that appears and
    // disappears between exports is worse than one that is always there and
    // explained — and they sit at the FAR RIGHT, past every measured figure, so
    // nobody reading left to right meets a hypothetical before a fact.
    ['Note', '"incl. SYSTAC" is what the week would need if every SYSTAC pursuit in it were won and staffed to its bid — a potential, not demand. Nothing has been awarded, no gap is computed from it, and no supply is measured against it.'],
    ['Note', 'Those three are also peak days, and the peak WITH pursuits can fall on a different day from the peak without them — so "incl. SYSTAC" minus the plain figure is what winning them adds at the busiest combined day, not the pursuits’ own total.'],
  ];

  const jobSheet = (
    jobs: GapAnalysis['travelJobs'],
    pursuits: GapAnalysis['systacTravelJobs'],
    kind: 'Travel' | 'Local',
  ): Cell[][] => [
    // ⚠️ A STATUS COLUMN, and it is not decoration. On screen the pursuits sit
    // under a colored band that says what they are; a spreadsheet has no band,
    // and it has a filter and a sort that will happily interleave a bid with
    // eight booked jobs. The column is what survives that.
    ['Job #', 'Job', 'Status', 'State', 'Leaders needed', 'Leaders figure', 'Leaders named', ...weekHeads],
    ...jobs.map((j): Cell[] => [
      j.jobNumber ?? '',
      j.title ?? '',
      'Scheduled',
      j.state ?? '',
      j.leadersNeeded,
      j.isLeadersOverride ? 'Override' : 'Default',
      j.leaders.length
        ? j.leaders.map((l) => (l.upcoming ? `${l.name} (upcoming)` : l.name)).join(', ')
        : 'unstaffed',
      ...j.weeklyCrew.map((wc) => wc.peak),
    ]),
    // ⚠️ Blank, never 0 or 'unstaffed', in the three leader cells. A bid states
    // crew and duration and says nothing about leaders, so there is no figure
    // to write — and 'unstaffed' would file this row under the very shortfall
    // the sheet exists to size.
    ...pursuits.map((p): Cell[] => [
      p.jobNumber ?? '',
      p.title ?? '',
      'SYSTAC pursuit — not awarded',
      p.state ?? '',
      blank(null), blank(null), blank(null),
      ...p.weeklyCrew.map((wc) => wc.peak),
    ]),
    [],
    ['Note', `${kind} jobs are those flagged ${kind === 'Travel' ? 'Travel' : 'Local'} on the crew schedule (projects.travel_job). Every scheduled job is on exactly one of these two sheets.`],
    ['Note', 'Weekly figures are that week’s PEAK crew day, not a total — do not sum across weeks.'],
    ['Note', 'State is parsed from the free-text job address and is best effort. There is no structured state field on a project.'],
    ['Note', '"unstaffed" means no leader is linked to the job in the Project Tracker — those slots are what open a leadership gap.'],
    ...(pursuits.length
      ? ([
          ['Note', 'Rows marked "SYSTAC pursuit" are BIDS. Nothing has been awarded, no gap is computed from them, and their leader cells are blank because a bid states crew and duration and says nothing about leaders.'],
          ['Note', 'A pursuit’s weekly figure is its own bid peak, spread flat across the bid duration on weekdays. It is NOT the "incl. SYSTAC" figure on the Week by week sheet, which is how much higher the whole week’s peak would go — the two answer different questions and will not always match.'],
          ['Note', 'Days a pursuit already has crew booked on are left to its scheduled row, so no day is counted twice.'],
        ] as Cell[][])
      : []),
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(summary), 'Summary');
  XLSX.utils.book_append_sheet(wb, sheet(weekRows), 'Week by week');
  // Header + job rows only — the notes below each table stay out of the filter.
  // ⚠️ The filter range covers the PURSUIT rows too. Leaving them below it
  // would put rows outside the filter that look like rows inside it — filter to
  // "OH" and the bids stay put, silently, which is the one way this sheet can
  // show somebody a figure they did not ask for.
  XLSX.utils.book_append_sheet(
    wb,
    sheet(jobSheet(data.travelJobs, data.systacTravelJobs, 'Travel'),
          { filter: true, filterRows: data.travelJobs.length + data.systacTravelJobs.length + 1 }),
    'Travel jobs',
  );
  XLSX.utils.book_append_sheet(
    wb,
    sheet(jobSheet(data.localJobs, data.systacLocalJobs, 'Local'),
          { filter: true, filterRows: data.localJobs.length + data.systacLocalJobs.length + 1 }),
    'Local jobs',
  );
  return wb;
}

export function exportGapXlsx(data: GapAnalysis, weeks: number, opts: GapExportOptions = {}) {
  save(buildGapWorkbook(data, weeks, opts), `manpower-gap-analysis-${stamp()}.xlsx`);
}
